import { useEffect, useState } from "react";
import { api } from "../api";
import { Btn, Card, ConfirmDialog, Field, Input, PasswordInput, SwitchRow,
         G_DARK } from "../components/Shared";

// ── Sign-in: the Keycloak connection and password sign-in ────────────────────
//
// Shaped after `SmtpCard` in NotificationsTab.jsx, for the same reasons: the
// secret is write-only (the server answers `oidc_client_secret_set`, never the
// value), a blank secret keeps the stored one, and *Test connection* makes a
// real request so a setting is proved rather than believed.
//
// **The guard rails are the server's.** Switching password sign-in off is
// refused there unless Keycloak is enabled, a test on the saved values passes
// at that moment, and the admin asking is signed in through Keycloak right
// now. A request built by hand skips this card, so nothing here is the
// protection; what this card adds is the one moment somebody reads what the
// switch does before it happens — hence the confirmation — and the server's
// own sentence when it refuses.

const URL = "/api/admin/auth-settings";

// The settings this card may change, in the order they are compared. The
// secret is not among them: it is sent only when somebody typed one.
const EDITABLE = [
  "oidc_enabled", "oidc_display_name", "oidc_issuer", "oidc_client_id",
  "oidc_allowed_domains", "oidc_self_registration",
  "local_login",
];

// Password sign-in is one switch, `on | off` (2026-09-28); the three-way
// `all | unprivileged | off` it replaced, and the staff second factor its
// middle value promised — the *Privileged ACR value* field with it — are
// gone. Switching off is what can lock somebody out, so that is what asks
// first; switching back on never can.
const PASSWORD_SENTENCE = {
  on:  "Every account without Keycloak signs in with a password; a Keycloak account never does.",
  off: "No password sign-in at all: the form is hidden, password resets are refused, and the portal mails no credential.",
};
// A stored retired value (`all`, `unprivileged`) reads as on, as on the server.
const passwordState = v => (v === "off" ? "off" : "on");

// `oidc_allowed_domains` is shown as one comma-separated line. If the server
// answers a list, the list shape is what goes back.
const domainsText = v => (Array.isArray(v) ? v.join(", ") : v ?? "");

function formFrom(settings) {
  return {
    oidc_enabled:           !!settings.oidc_enabled,
    oidc_display_name:      settings.oidc_display_name ?? "",
    oidc_issuer:            settings.oidc_issuer ?? "",
    oidc_client_id:         settings.oidc_client_id ?? "",
    oidc_allowed_domains:   domainsText(settings.oidc_allowed_domains),
    oidc_self_registration: !!settings.oidc_self_registration,
    local_login:            passwordState(settings.local_login),
    // Always blank, whatever the server sent: a response carrying the secret by
    // mistake must still not put it on screen.
    oidc_client_secret:     "",
  };
}

/** Only what differs from what the server last said — a PATCH carrying the
 *  stored value again is not a no-op here: `local_login` in the body is what
 *  the server's guard rails run on, so resending it with a display-name edit
 *  could be refused for a switch nobody touched. */
function changesFrom(form, settings) {
  const stored = formFrom(settings);
  const out = {};
  for (const key of EDITABLE) {
    if (form[key] === stored[key]) continue;
    out[key] = key === "oidc_allowed_domains" && Array.isArray(settings.oidc_allowed_domains)
      ? form[key].split(",").map(s => s.trim()).filter(Boolean)
      : form[key];
  }
  if (form.oidc_client_secret) out.oidc_client_secret = form.oidc_client_secret;
  return out;
}

// The server's sentence. A FastAPI validation error carries a list, which
// `api.js` leaves on `err.detail` with a bare "HTTP 422" as the message.
function sentenceOf(e) {
  if (Array.isArray(e?.detail)) {
    const msgs = e.detail.map(d => d && d.msg).filter(Boolean);
    if (msgs.length) return msgs.join(" ");
  }
  return e?.message || "The request failed.";
}

const count = v => (Array.isArray(v) ? v.length : Number(v) || 0);

function ImpactNote({ impact }) {
  const noEmail = count(impact?.no_email);
  const shared  = count(impact?.shared_email);
  const total   = noEmail + shared;
  return (
    <p style={{ margin: "8px 0 0", fontSize: 13, color: total ? "#92400e" : "#374151" }}>
      {total === 1 ? "1 active account would lose its way in" : `${total} active accounts would lose their way in`}
      {`: ${noEmail} with no e-mail, ${shared} sharing an e-mail with another account. `}
      An account with an e-mail of its own is linked on its first Keycloak sign-in.
    </p>
  );
}

// Sign-ins the portal refused because Keycloak's token did not carry
// `email_verified: true`. The rule stays; the fix is in Keycloak, and only its
// administrator can make it. Nothing on this card can see the realm — Test
// connection reads discovery and JWKS only — so the line names the two
// Keycloak settings rather than a check here. Hidden at 0 or when absent.
function UnverifiedRefusals({ n }) {
  const refused = Number(n) || 0;
  if (refused <= 0) return null;
  return (
    <div data-testid="unverified-refusals" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.25)", color: "#92400e", fontSize: 13 }}>
      {refused === 1 ? "1 sign-in was refused in the last 7 days" : `${refused} sign-ins were refused in the last 7 days`}
      {" because Keycloak had not verified the address. The fix is in Keycloak, not here: "}
      tick <strong>Email verified</strong> on the user, or turn on <strong>Trust email</strong> on
      the identity provider they come through.
    </div>
  );
}

// Visually hidden, still read out: `Btn` forwards no aria-label, so the words
// that tell two "Copy" buttons apart sit inside the button itself.
const SR_ONLY = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
                  overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 };

// An address the portal knows and Keycloak's admin must enter: read-only, with
// a button that copies it.
function CopyField({ label, name, hint, value, onCopy, copied }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Field label={label} hint={hint}>
        <div style={{ display: "flex", gap: 8 }}>
          <Input value={value || ""} readOnly />
          <Btn variant="secondary" onClick={onCopy}>Copy<span style={SR_ONLY}> {name}</span></Btn>
        </div>
      </Field>
      {copied && <div role="status" style={{ marginTop: 6, fontSize: 12, color: "#374151" }}>{copied}</div>}
    </div>
  );
}

function SignInForm({ settings, onSaved }) {
  const [form, setForm]       = useState(() => formFrom(settings));
  const [busy, setBusy]       = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);
  const [confirming, setConfirming] = useState(null);   // the payload awaiting a yes
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied]   = useState(null);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const send = async payload => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.patch(URL, payload);
      onSaved(result);
      setForm(formFrom(result));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(sentenceOf(e));
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  const handleSave = () => {
    setError(null);
    setSaved(false);
    setNotice(null);
    const payload = changesFrom(form, settings);
    if (Object.keys(payload).length === 0) {
      setNotice("Nothing has changed.");
      return;
    }
    if (payload.local_login === "off") {
      setConfirming(payload);
      return;
    }
    send(payload);
  };

  // Tests the SAVED values, which is what the server's refusal to enable is
  // measured against — an unsaved edit is not what anybody will sign in with.
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.post("/api/admin/auth/oidc/test", {});
      setTestResult({ ok: !!result?.ok, detail: result?.detail || "" });
    } catch (e) {
      setTestResult({ ok: false, detail: sentenceOf(e) });
    } finally {
      setTesting(false);
    }
  };

  // `copied` is keyed by the field, so the sentence lands under the address
  // that was copied rather than under both.
  const handleCopy = key => async () => {
    try {
      await navigator.clipboard.writeText(settings[key] || "");
      setCopied({ key, text: "Copied." });
    } catch {
      setCopied({ key, text: "Could not copy — select the address and copy it by hand." });
    }
  };

  return (
    <Card style={{ marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>Keycloak</h3>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: "#6b7280" }}>
        The portal is one more confidential client in the facility's Keycloak realm. A person
        signing in through it is known by their verified e-mail.
      </p>
      {error && (
        <div role="alert" style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      <UnverifiedRefusals n={settings.unverified_refusals_7d} />

      <CopyField label="Callback URL" name="callback URL"
                 hint="Copy this into the Keycloak client's valid redirect URIs."
                 value={settings.callback_url} onCopy={handleCopy("callback_url")}
                 copied={copied?.key === "callback_url" ? copied.text : null} />
      {/* Where Keycloak sends somebody back after "use a different account". It
          refuses any address the client does not list, and only its admin can
          list one — so the card shows it, the way it shows the callback. */}
      <CopyField label="Post-logout redirect URI" name="post-logout redirect URI"
                 hint="Copy this into the Keycloak client's valid post logout redirect URIs."
                 value={settings.post_logout_redirect_uri}
                 onCopy={handleCopy("post_logout_redirect_uri")}
                 copied={copied?.key === "post_logout_redirect_uri" ? copied.text : null} />

      <div data-col2 style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Field label="Button label" hint="Shown on the sign-in page as “Sign in with …”.">
          <Input value={form.oidc_display_name} onChange={set("oidc_display_name")} placeholder="Keycloak" />
        </Field>
        <Field label="Issuer URL" hint="The realm's address; https, except for localhost.">
          <Input value={form.oidc_issuer} onChange={set("oidc_issuer")} placeholder="https://login.example.org/realms/boku" />
        </Field>
        <Field label="Client ID">
          <Input value={form.oidc_client_id} onChange={set("oidc_client_id")} />
        </Field>
        <Field label="Client secret" hint={settings.oidc_client_secret_set ? "Currently set — leave blank to keep it" : "Not set"}>
          <PasswordInput value={form.oidc_client_secret} onChange={set("oidc_client_secret")}
                         placeholder="••••••••" autoComplete="new-password" />
        </Field>
        <Field label="Allowed e-mail domains" hint="Comma-separated. Blank admits any domain.">
          <Input value={form.oidc_allowed_domains} onChange={set("oidc_allowed_domains")} placeholder="boku.ac.at" />
        </Field>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
        <SwitchRow
          label="Sign-in through Keycloak enabled"
          checked={form.oidc_enabled}
          onChange={v => setForm(f => ({ ...f, oidc_enabled: v }))}
          hint="Refused while a connection test on the saved values fails."
        />
        <SwitchRow
          label="Self-registration"
          checked={form.oidc_self_registration}
          onChange={v => setForm(f => ({ ...f, oidc_self_registration: v }))}
          hint="On: somebody with no portal account gets one, as a user in no group. Off: they are told to ask the facility."
        />
      </div>

      <div style={{ marginBottom: 20, paddingTop: 16, borderTop: "1px solid #f3f4f6" }}>
        <SwitchRow
          label="Password sign-in"
          checked={form.local_login !== "off"}
          onChange={v => setForm(f => ({ ...f, local_login: v ? "on" : "off" }))}
          hint={PASSWORD_SENTENCE[form.local_login]}
        />
        {form.local_login === "off" && <ImpactNote impact={settings.local_login_impact} />}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Btn onClick={handleSave} disabled={busy}>{busy ? "Saving…" : saved ? "✓ Saved" : "Save"}</Btn>
        <Btn variant="secondary" onClick={handleTest} disabled={testing}>
          {testing ? "Testing…" : "Test connection"}
        </Btn>
        {notice && <span role="status" style={{ fontSize: 13, color: "#374151" }}>{notice}</span>}
      </div>
      {testResult && (
        <div role="status" style={{ marginTop: 10, fontSize: 13, color: testResult.ok ? G_DARK : "#b91c1c" }}>
          {testResult.ok ? "✓ " : "✗ "}{testResult.detail || (testResult.ok ? "The connection works." : "The connection failed.")}
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title="Switch password sign-in off?"
          confirmLabel="Switch password sign-in off"
          busyLabel="Saving…"
          busy={busy}
          onConfirm={() => send(confirming)}
          onClose={() => setConfirming(null)}
        >
          <p style={{ margin: "0 0 8px", fontSize: 14, color: "#374151" }}>
            {PASSWORD_SENTENCE.off}
          </p>
          <ImpactNote impact={settings.local_login_impact} />
          <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280" }}>
            If Keycloak then stops answering, the way back is the break-glass command on the
            cluster, not this page.
          </p>
        </ConfirmDialog>
      )}
    </Card>
  );
}

export default function SignInCard() {
  const [settings, setSettings] = useState(null);
  const [error, setError]       = useState(null);

  useEffect(() => {
    let live = true;
    api.get(URL)
      .then(s => { if (live) setSettings(s); })
      .catch(e => { if (live) setError(sentenceOf(e)); });
    return () => { live = false; };
  }, []);

  if (error) {
    return (
      <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
        {error}
      </div>
    );
  }
  if (!settings) return <div style={{ color: "#6b7280", fontSize: 13 }}>Loading…</div>;
  return <SignInForm settings={settings} onSaved={setSettings} />;
}
